/**
 * The own-key prompt shown when a capped AI feature answers 402 AI_CAP_REACHED.
 *
 * It states the allowance and this month's usage from the API's budget (never
 * a figure of its own) and links to Settings → AI & integrations → AI usage,
 * where an admin adds the company's own Anthropic key. An amber notice, not a
 * red error: nothing failed, the allowance is used up.
 */
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import type { AiBudget } from '../../types/ewAiQuote';
import { fmtCurrency } from '../../lib/display-labels';

/** AI usage is always billed in US dollars, whatever the tenant's currency. */
const usd = (n: number | null | undefined) => fmtCurrency(n, 'USD');

export function AiCapPrompt({ budget }: { budget: Partial<AiBudget> | null }) {
  // A saved key that calls do not run on yet (key.inUse false) cannot lift the cap, so the copy says so.
  const keySaved = !!budget?.key?.set && !budget?.key?.inUse;
  return (
    <div role="alert" data-testid="ai-cap-prompt" className="rounded-fi-control border border-fi-amber/50 bg-fi-amber/10 p-4 text-sm text-fieldiq-text">
      <p className="flex items-center gap-2 font-bold text-white">
        <KeyRound className="size-4 text-fi-amber" aria-hidden="true" />
        This month's AI allowance is used up
      </p>
      <p className="mt-1">
        Your company has used {usd(budget?.countedUsd)} of its {usd(budget?.capUsd)} monthly AI allowance on FieldIQ's key.
        {keySaved
          ? ` Your own key ending ${budget?.key?.last4 ?? '—'} is saved, but AI calls do not run on it yet. Until they do, AI quoting starts again on the 1st of next month.`
          : ' To keep using AI quoting this month, an admin can add your own Anthropic API key. Calls on your own key do not count toward the allowance.'}
      </p>
      <Link to="/settings/ai-usage" className="mt-3 inline-flex font-bold text-fi-amber underline underline-offset-2">
        {keySaved ? 'See AI usage in Settings' : 'Add your own key in Settings → AI usage'}
      </Link>
    </div>
  );
}
